import React, { useState } from "react";
import {
  Eye,
  XCircle,
  Mail,
  Phone,
  Ticket,
  User,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  Users,
} from "lucide-react";
import { toast } from "react-toastify";

/**
 * Attendees Table Component
 * Displays attendees in a data table format.
 * Supports displayRows: each row is either { type: 'single', attendee } or { type: 'group', groupKey, attendees }.
 * If displayRows is not provided, falls back to treating attendees as flat list (each Single).
 */
const ticketOf = (registration) =>
  registration?.ticket_type && typeof registration.ticket_type === "object"
    ? registration.ticket_type
    : null;

const registrationName = (registration) => {
  if (registration?.registration_type === "group" || registration?.group_info) {
    return (
      registration.group_info?.organization_name ||
      registration.group_info?.primary_contact_name ||
      "Group registration"
    );
  }
  const name = [registration?.first_name, registration?.last_name].filter(Boolean).join(" ");
  return name || registration?.name || "N/A";
};

const registrationEmail = (registration) =>
  registration?.registration_type === "group" || registration?.group_info
    ? registration.group_info?.primary_contact_email || registration.email
    : registration?.email;

const registrationPhone = (registration) =>
  registration?.registration_type === "group" || registration?.group_info
    ? registration.group_info?.primary_contact_phone || registration.contact_number || registration.phone
    : registration?.contact_number || registration?.phone;

const ticketLabel = (registration) =>
  ticketOf(registration)?.name ||
  (typeof registration?.ticket_type === "string" ? registration.ticket_type : "N/A");

const amountLabel = (registration) => {
  const ticket = ticketOf(registration);
  const currency = String(ticket?.currency || registration?.currency || "").trim().toUpperCase();
  const hasTotal =
    registration?.total_amount !== null &&
    registration?.total_amount !== undefined &&
    registration?.total_amount !== "";
  const isGroup =
    registration?.registration_type === "group" ||
    (Array.isArray(registration?.attendees) && registration.attendees.length > 0);
  const quantity = isGroup
    ? (registration.attendees?.length || Number(registration.group_info?.number_of_attendees) || 1)
    : 1;
  const unit = hasTotal
    ? Number(registration.total_amount)
    : Number(ticket?.price ?? registration?.amount_paid);
  if (Number.isNaN(unit)) return "N/A";
  const amount = hasTotal ? unit : unit * quantity;
  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${currency} ${formatted}` : formatted;
};

const AttendeesTable = ({
  attendees: attendeesProp,
  displayRows: displayRowsProp,
  onView,
  onCancel,
  currentPage,
  itemsPerPage,
  totalCount,
  onPageChange,
  onItemsPerPageChange,
  copiedId,
  onCopy,
}) => {
  const [expandedGroupKey, setExpandedGroupKey] = useState(null);

  // Use displayRows if provided; otherwise build single rows from attendees
  const displayRows = displayRowsProp ?? (Array.isArray(attendeesProp) ? attendeesProp.map((attendee) => ({ type: "single", attendee })) : []);
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalCount);

  const getPaymentStatusBadge = (status) => {
    const statusLower = (status || "").toLowerCase();
    switch (statusLower) {
      case "paid":
      case "completed":
        return {
          label: "Paid",
          color: "bg-green-100 text-green-700",
        };
      case "pending":
        return {
          label: "Pending",
          color: "bg-orange-100 text-orange-700",
        };
      case "failed":
        return {
          label: "Failed",
          color: "bg-red-100 text-red-700",
        };
      case "refunded":
        return {
          label: "Refunded",
          color: "bg-gray-100 text-gray-700",
        };
      default:
        return {
          label: status || "Unknown",
          color: "bg-gray-100 text-gray-700",
        };
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard!`);
      if (onCopy) onCopy(text);
    });
  };

  const shortenId = (id) => {
    if (!id) return "N/A";
    if (id.length <= 12) return id;
    return `${id.substring(0, 8)}...${id.substring(id.length - 4)}`;
  };

  if (displayRows.length === 0) {
    return (
      <div className="overflow-hidden bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="py-16 text-center">
          <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-gray-100 rounded-full">
            <User className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            No attendees found
          </h3>
          <p className="mx-auto max-w-sm text-sm text-gray-600">
            Try adjusting your filters to find what you're looking for.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
      {/* Table Header */}
      <div className="px-4 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 sm:px-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-base font-bold text-gray-900 sm:text-lg">
              Attendees List
            </h3>
            <p className="mt-0.5 text-sm text-gray-600">
              {totalCount} {totalCount === 1 ? "attendee" : "attendees"} found
            </p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-9 px-2 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                {/* Expand for groups */}
              </th>
              <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                Registration Type
              </th>
              <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                Attendee ID
              </th>
              <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                Name
              </th>
              <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                Contact
              </th>
              <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                Ticket Type
              </th>
              <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                Amount Paid
              </th>
              <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                Payment Status
              </th>
              <th className="px-6 py-4 text-xs font-bold tracking-wider text-right text-gray-700 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {displayRows.map((row, rowIndex) => {
              if (row.type === "group") {
                const { groupKey, attendees: groupAttendees, registration } = row;
                const record = registration || groupAttendees[0] || {};
                const isExpanded = expandedGroupKey === groupKey;
                const paymentStatus = getPaymentStatusBadge(record.payment_status);
                const people = Array.isArray(record.attendees) && record.attendees.length > 0
                  ? record.attendees
                  : groupAttendees;
                const groupTitle = registrationName(record);
                const groupEmail = registrationEmail(record);
                const groupPhone = registrationPhone(record);
                return (
                  <React.Fragment key={groupKey ?? rowIndex}>
                    <tr className="bg-purple-50/50 hover:bg-purple-50 transition-colors">
                      <td className="px-2 py-3 align-top">
                        <button
                          type="button"
                          onClick={() => setExpandedGroupKey(isExpanded ? null : groupKey)}
                          className="p-1.5 rounded-lg text-purple-600 hover:bg-purple-100"
                          title={isExpanded ? "Collapse" : "Expand group"}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="px-6 py-3 align-top">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full">
                          <Users className="w-3.5 h-3.5" />
                          Group
                        </span>
                      </td>
                      <td className="px-6 py-3 align-top">
                        <span className="font-mono text-sm text-gray-600">{shortenId(record.slug || groupKey)}</span>
                      </td>
                      <td className="px-6 py-3 align-top">
                        <div>
                          <span className="text-sm font-medium text-gray-900">{groupTitle}</span>
                          <p className="text-xs text-gray-500">{people.length} attendee{people.length !== 1 ? "s" : ""}</p>
                        </div>
                      </td>
                      <td className="px-6 py-3 align-top">
                        <div className="space-y-0.5">
                          {groupEmail && <div className="flex items-center text-xs text-gray-900"><Mail className="w-3 h-3 mr-1.5 text-gray-400" /><span className="truncate">{groupEmail}</span></div>}
                          {groupPhone && <div className="flex items-center text-xs text-gray-600"><Phone className="w-3 h-3 mr-1.5 text-gray-400" /><span className="truncate">{groupPhone}</span></div>}
                          {!groupEmail && !groupPhone && <span className="text-xs text-gray-400">N/A</span>}
                        </div>
                      </td>
                      <td className="px-6 py-3 align-top">
                        <span className="px-2 py-0.5 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full">{ticketLabel(record)}</span>
                      </td>
                      <td className="px-6 py-3 align-top">
                        <span className="text-sm font-semibold text-gray-900">{amountLabel(record)}</span>
                      </td>
                      <td className="px-6 py-3 align-top">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${paymentStatus.color}`}>
                          {paymentStatus.label}
                        </span>
                      </td>
                      <td className="px-6 py-3 align-top text-right">
                        <div className="flex justify-end items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => onView(record)}
                            className="flex items-center px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                            title="View Details"
                          >
                            <Eye className="mr-1 w-3 h-3" /> View
                          </button>
                          <button
                            type="button"
                            onClick={() => setExpandedGroupKey(isExpanded ? null : groupKey)}
                            className="text-xs font-medium text-purple-600 hover:text-purple-700"
                          >
                            {isExpanded ? "Collapse" : "Expand"}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && people.map((person, personIndex) => {
                      const personName = [person.first_name, person.last_name].filter(Boolean).join(" ") || person.name || "N/A";
                      const personEmail = person.email;
                      const personPhone = person.contact_number || person.phone;
                      return (
                        <tr key={`${person.email || personName}-${personIndex}`} className="bg-gray-50/80 hover:bg-purple-50/30">
                          <td className="px-2 py-2" />
                          <td className="px-6 py-2 text-xs text-gray-500">Member</td>
                          <td className="px-6 py-2 text-xs text-gray-400">—</td>
                          <td className="px-6 py-2">
                            <span className="text-sm font-medium text-gray-900">{personName}</span>
                            {person.profession && <p className="text-xs text-gray-500">{person.profession}</p>}
                          </td>
                          <td className="px-6 py-2">
                            <div className="space-y-0.5">
                              {personEmail && <div className="flex items-center text-xs text-gray-900"><Mail className="w-3 h-3 mr-1.5 text-gray-400" /><span className="truncate">{personEmail}</span></div>}
                              {personPhone && <div className="flex items-center text-xs text-gray-600"><Phone className="w-3 h-3 mr-1.5 text-gray-400" /><span className="truncate">{personPhone}</span></div>}
                            </div>
                          </td>
                          <td className="px-6 py-2 text-xs text-gray-500" colSpan={4}>
                            {person.nationality || person.departure_city || ""}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              }
              const attendee = row.attendee;
              const paymentStatus = getPaymentStatusBadge(
                attendee.payment_status
              );
              const attendeeId =
                attendee.slug || attendee.attendee_id || attendee.id || attendee.ticket_id;
              const name = registrationName(attendee);
              const email = registrationEmail(attendee);
              const phone = registrationPhone(attendee);

              return (
                <tr
                  key={attendeeId}
                  className="transition-colors duration-150 hover:bg-purple-50/30"
                >
                  <td className="w-9 px-2 py-4" />
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold text-gray-700 bg-gray-100 rounded-full">
                      Single
                    </span>
                  </td>
                  {/* Attendee ID */}
                  <td className="px-6 py-4">
                    <button
                      onClick={() => copyToClipboard(attendeeId, "Attendee ID")}
                      className="flex items-center font-mono text-sm text-purple-600 hover:text-purple-700 group"
                      title={attendeeId}
                    >
                      <span className="bg-purple-50 px-2 py-1 rounded group-hover:bg-purple-100 transition-colors">
                        {shortenId(attendeeId)}
                      </span>
                      {copiedId === attendeeId ? (
                        <Check className="ml-1.5 w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="ml-1.5 w-4 h-4 opacity-0 transition-opacity group-hover:opacity-100" />
                      )}
                    </button>
                  </td>

                  {/* Name */}
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex overflow-hidden flex-shrink-0 justify-center items-center w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg shadow-sm">
                        <span className="text-sm font-semibold text-white">
                          {(name || "U")
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .substring(0, 2)
                            .toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-gray-900 truncate">
                          {name}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Contact */}
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      {email && (
                        <div className="flex items-center text-sm text-gray-900">
                          <Mail className="w-3.5 h-3.5 mr-2 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{email}</span>
                        </div>
                      )}
                      {phone && (
                        <button
                          onClick={() =>
                            copyToClipboard(phone, "Phone number")
                          }
                          className="flex items-center text-sm text-gray-600 transition-colors hover:text-purple-600 group"
                        >
                          <Phone className="w-3.5 h-3.5 mr-2 text-gray-400 group-hover:text-purple-500 flex-shrink-0" />
                          <span className="truncate">{phone}</span>
                          {copiedId === phone ? (
                            <Check className="ml-1 w-3 h-3 text-green-600" />
                          ) : (
                            <Copy className="ml-1 w-3 h-3 opacity-0 transition-opacity group-hover:opacity-100" />
                          )}
                        </button>
                      )}
                      {!email && !phone && (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </div>
                  </td>

                  {/* Ticket Type */}
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Ticket className="w-4 h-4 mr-2 text-purple-600 flex-shrink-0" />
                      <span className="px-2.5 py-1 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full capitalize">
                        {ticketLabel(attendee)}
                      </span>
                    </div>
                  </td>

                  {/* Amount Paid */}
                  <td className="px-6 py-4">
                    <div className="text-sm font-semibold text-gray-900">
                      {amountLabel(attendee)}
                    </div>
                  </td>

                  {/* Payment Status */}
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${paymentStatus.color}`}
                    >
                      {paymentStatus.label}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4">
                    <div className="flex justify-end items-center space-x-2">
                      <button
                        onClick={() => onView(attendee)}
                        className="flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg transition-colors hover:bg-blue-100"
                        title="View Details"
                      >
                        <Eye className="mr-1.5 w-3.5 h-3.5" />
                        View
                      </button>
                      <button
                        onClick={() => onCancel(attendee)}
                        disabled={
                          attendee.payment_status === "refunded" ||
                          attendee.status === "cancelled"
                        }
                        className="flex items-center px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg transition-colors hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Cancel Ticket"
                      >
                        <XCircle className="mr-1.5 w-3.5 h-3.5" />
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-4 bg-gray-50 border-t border-gray-100 sm:px-6">
          <div className="flex flex-col justify-between items-center space-y-4 sm:flex-row sm:space-y-0">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{startIndex}</span> to{" "}
              <span className="font-medium">{endIndex}</span> of{" "}
              <span className="font-medium">{totalCount}</span> results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 text-gray-400 bg-white border border-gray-300 rounded-lg transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange(pageNum)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                        currentPage === pageNum
                          ? "text-white bg-purple-600"
                          : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-400 bg-white border border-gray-300 rounded-lg transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendeesTable;
